/**
 * Entry Card Component
 *
 * Displays an entry summary in the list view.
 */

import { Link } from 'react-router-dom';
import type { Entry } from '@/types/entry';
import {
  ENTRY_TYPE_ICONS,
  ENTRY_TYPE_LABELS,
  SOURCE_SYSTEM_LABELS,
  formatDuration,
} from '@/types/entry';

interface EntryCardProps {
  entry: Entry;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '--';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NZ', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EntryCard({ entry }: EntryCardProps) {
  const typeIcon = ENTRY_TYPE_ICONS[entry.effectiveEntryType as keyof typeof ENTRY_TYPE_ICONS] || '📄';
  const typeLabel = ENTRY_TYPE_LABELS[entry.effectiveEntryType as keyof typeof ENTRY_TYPE_LABELS] || entry.effectiveEntryType;
  const sourceLabel = SOURCE_SYSTEM_LABELS[entry.sourceSystem as keyof typeof SOURCE_SYSTEM_LABELS] || entry.sourceSystem;

  const hasOverrides = entry.overrideMetadata?.hasUserOverrides;

  return (
    <Link
      to={`/entries/${entry.id}`}
      className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-indigo-300 hover:shadow-md transition-all"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl flex-shrink-0" title={typeLabel}>
            {typeIcon}
          </span>
          <h3 className="font-medium text-gray-900 truncate">
            {entry.effectiveTitle}
            {hasOverrides && (
              <span className="ml-1.5 text-xs text-indigo-500" title="User edited">
                ✏️
              </span>
            )}
          </h3>
        </div>

        {/* Source badge */}
        <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
          {sourceLabel}
        </span>
      </div>

      {/* Synopsis */}
      {entry.effectiveSynopsis && (
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">
          {entry.effectiveSynopsis}
        </p>
      )}

      {/* Tags */}
      {entry.effectiveTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {entry.effectiveTags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
            >
              {tag}
            </span>
          ))}
          {entry.effectiveTags.length > 4 && (
            <span className="text-xs text-gray-400">
              +{entry.effectiveTags.length - 4} more
            </span>
          )}
        </div>
      )}

      {/* Meta info */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>{formatDate(entry.recordedAt)}</span>
        {entry.durationSeconds && (
          <span>⏱ {formatDuration(entry.durationSeconds)}</span>
        )}
        {entry.sourceMetadata?.wordCount && (
          <span>📝 {entry.sourceMetadata.wordCount.toLocaleString()} words</span>
        )}
        {entry.processingStatus !== 'complete' && (
          <span className={`capitalize ${
            entry.processingStatus === 'failed' ? 'text-red-500' :
            entry.processingStatus === 'processing' ? 'text-yellow-500' :
            'text-gray-400'
          }`}>
            {entry.processingStatus}
          </span>
        )}
      </div>
    </Link>
  );
}

